import useLocalOverwriteCycles from '@/composables/saved-views/local-overwrite-cycles';
import useLocalOverwriteLockCycleOnView from '@/composables/saved-views/local-overwrite-lock-cycle-on-view';
import useLocalOverwritesParams from '@/composables/saved-views/local-overwrite-params';
import useLocalOverwritesSort from '@/composables/saved-views/local-overwrite-sort';
import { computed } from 'vue';
import { copy } from 'shared/lib/copy';
import { findInArray } from 'shared/lib/array/array';

export default function useViewsRepoLocal(repo, loggedInUserAccount) {
  const localOverwritesParamsSvc = useLocalOverwritesParams(localStorage, loggedInUserAccount);
  const localOverwritesSortSvc = useLocalOverwritesSort(localStorage, loggedInUserAccount);
  const localOverwritesCyclesSvc = useLocalOverwriteCycles(localStorage, loggedInUserAccount);
  const localOverwritesLockCycleOnViewSvc = useLocalOverwriteLockCycleOnView(localStorage, loggedInUserAccount);

  localOverwritesSortSvc.initOverwrites();
  localOverwritesParamsSvc.initOverwrites();
  localOverwritesCyclesSvc.initOverwrites();
  localOverwritesLockCycleOnViewSvc.initOverwrites();

  const views = computed(() => repo.views.value.map(localOverwritesParamsSvc.mergeWithLocalOverwrites).map(localOverwritesCyclesSvc.mergeWithLocalOverwrites).map(localOverwritesLockCycleOnViewSvc.mergeWithLocalOverwrites).map((view) => {
    if (view.isTemp) {
      return localOverwritesSortSvc.mergeWithLocalOverwrites(view);
    }

    return view;
  }));

  const resetView = (id, { onlyParams = [] } = { onlyParams: [] }) => {
    const view = views.value.find((v) => v.uid === id);
    localOverwritesParamsSvc.removeLocalOverwrite(view);
    if (onlyParams.length !== 0) {
      const params = view.params;
      for (let i = 0; i < onlyParams.length; i++) {
        delete params[onlyParams[i]];
      }
      localOverwritesParamsSvc.saveOverwrite({ ...view, params });
    } else {
      localOverwritesCyclesSvc.removeLocalOverwrite(view);
      localOverwritesLockCycleOnViewSvc.removeLocalOverwrite(view);
    }
    return views.value.find((v) => v.uid === id);
  };

  const updateView = (v, { onlyParams = [] } = { onlyParams: [] }) => {
    let toUpdate = v;
    if (onlyParams.length !== 0) {
      toUpdate = { uid: v.uid, isTemp: v.isTemp, params: copy(repo.views.value.find((v) => v.uid === toUpdate.uid).params) };
      for (let i = 0; i < onlyParams.length; i++) {
        toUpdate.params[onlyParams[i]] = v.params[onlyParams[i]];
      }
    }
    return repo.updateView(toUpdate);
  };

  const setParams = (view, params) => {
    localOverwritesParamsSvc.saveOverwrite({ ...view, params });
  };

  const updateSort = (view, sortOperation) => {
    if (!view.canEdit) {
      localOverwritesSortSvc.saveOverwrite(view);
      return new Promise((resolve) => { resolve(view); });
    }

    return repo.updateView({ uid: view.uid, isTemp: view.isTemp, goalSort: view.goalSort }, {}, { sortOperation });
  };

  const updateCycles = (view, goalCycles, unassignedGoalCycleSelected) => {
    if (!view.canEdit) {
      localOverwritesCyclesSvc.saveOverwrite({
        ...view,
        goalCycles,
        unassignedGoalCycleSelected,
      });
      return;
    }

    repo.updateView({ uid: view.uid, isTemp: view.isTemp, goalCycles, unassignedGoalCycleSelected });
  };

  const updateLockCyclesOnView = (view, lockCycleOnView) => {
    if (!view.canEdit) {
      localOverwritesLockCycleOnViewSvc.saveOverwrite({ ...view, lockCycleOnView });
      return;
    }

    repo.updateView({ uid: view.uid, isTemp: view.isTemp, lockCycleOnView });
  };

  const unsavedChanges = (view) => {
    const f = findInArray({ haystack: repo.views.value, needle: view.uid });
    if (f === null) {
      return [];
    }

    return [
      ...localOverwritesParamsSvc.hasLocalOverwrite(f),
      ...localOverwritesCyclesSvc.hasLocalOverwrite(f),
      ...localOverwritesLockCycleOnViewSvc.hasLocalOverwrite(f),
    ];
  };

  return {
    createLoading: repo.createLoading,
    createViews: repo.createViews,

    loadingUpdateView: repo.loadingUpdateView,
    updateView,

    loadingDeleteView: repo.loadingDeleteView,
    deleteView: repo.deleteView,

    setParams,
    updateSort,
    updateCycles,
    updateLockCyclesOnView,

    resetView,

    unsavedChanges,

    views,
  };
}
